import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './responsive.css';

// Components
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Home from './pages/Home/Home';
import TermsAndConditions from './pages/Legal/TermsAndConditions';
import Support from './pages/Support/Support';
import LearnMore from './pages/LearnMore/LearnMore';
import GetStarted from './pages/GetStarted/GetStarted';

// Dashboard Pages
import AdminDashboard from './pages/Dashboard/AdminDashboard';
import DistributorDashboard from './pages/Dashboard/DistributorDashboard';
import UserDashboard from './pages/Dashboard/UserDashboard';
import WalletManagement from './pages/Admin/WalletManagement';
import PaymentRequests from './pages/Admin/PaymentRequests';
import UserManagement from './pages/Admin/UserManagement';
import KycManagement from './pages/Admin/KycManagement';
import AdminReports from './pages/Admin/AdminReports';
import UserReports from './pages/User/UserReports';
import DistributorReports from './pages/Distributor/DistributorReports';
import AdminLoginLogs from './pages/Admin/AdminLoginLogs';
import UserLoginLogs from './pages/User/UserLoginLogs';
import DistributorLoginLogs from './pages/Distributor/DistributorLoginLogs';
import UpdatePayment from './pages/Wallet/UpdatePayment';
import MyRequests from './pages/Wallet/MyRequests';
import RechargeBillPage from './pages/Recharges/RechargeBillPage';



// ✅ Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading spinner while authentication is being checked
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">
            <h5>Authenticating...</h5>
            <p className="text-muted">Please wait while we verify your credentials</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// ✅ Dashboard Route Router
const DashboardRoute = () => {
  const { user } = useAuth();

  switch (user?.role) {
    case 'admin':
      return <AdminDashboard />;
    case 'distributor':
      return <DistributorDashboard />;
    case 'user':
      return <UserDashboard />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// ✅ Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* ✅ Wrap App in index.js, NOT here */}
        <Router>
          <div className="App">
            <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />

            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/support" element={<Support />} />
              <Route path="/learn-more" element={<LearnMore />} />
              <Route path="/legal/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/get-started" element={<GetStarted />} />

              {/* Protected Dashboard Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardRoute />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/wallets"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <WalletManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payment-requests"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <PaymentRequests />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/login-logs"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminLoginLogs />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/kyc"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <KycManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Distributor Routes */}
              <Route
                path="/distributor/*"
                element={
                  <ProtectedRoute allowedRoles={['distributor']}>
                    <Layout>
                      <DistributorDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/distributor/reports"
                element={
                  <ProtectedRoute allowedRoles={['distributor']}>
                    <Layout>
                      <DistributorReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/distributor/login-logs"
                element={
                  <ProtectedRoute allowedRoles={['distributor']}>
                    <Layout>
                      <DistributorLoginLogs />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* User Routes */}
              <Route
                path="/user/*"
                element={
                  <ProtectedRoute allowedRoles={['user']}>
                    <Layout>
                      <UserDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/reports"
                element={
                  <ProtectedRoute allowedRoles={['user']}>
                    <Layout>
                      <UserReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/login-logs"
                element={
                  <ProtectedRoute allowedRoles={['user']}>
                    <Layout>
                      <UserLoginLogs />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services/recharge"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RechargeBillPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Wallet Routes (for users and distributors) */}
              <Route
                path="/wallet/update-payment"
                element={
                  <ProtectedRoute allowedRoles={['user', 'distributor']}>
                    <Layout>
                      <UpdatePayment />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wallet/my-requests"
                element={
                  <ProtectedRoute allowedRoles={['user', 'distributor']}>
                    <Layout>
                      <MyRequests />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Error Pages */}
              <Route path="/unauthorized" element={<div>Unauthorized Access</div>} />
              <Route path="/404" element={<div>Page Not Found</div>} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </div>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
